import Flickity from 'flickity'
import { getSizedImageUrl } from '@shopify/theme-images'
import { productPrice, productPointsValue } from '../helpers/product'
import { yieldToMain } from '../helpers/yield-to-main'

import axios from 'axios'

import {
  defineStore
} from 'pinia'

const SKIO_APP_ID = 'SKIO'

const EU_STORE_DOMAINS = ['sneak-eu.myshopify.com']

const isSubscriptionsMarket = () => {
  const domain = window.theme?.shop?.permanentDomain || ''
  return !EU_STORE_DOMAINS.includes(domain)
}

// Filter selling plan groups based on theme setting (Skio UK/US; none on EU)
const filterSellingPlanGroups = (sellingPlanGroups) => {
  if (!isSubscriptionsMarket()) return []
  if (!sellingPlanGroups || !sellingPlanGroups.length) return sellingPlanGroups

  return sellingPlanGroups.filter(group => group.app_id === SKIO_APP_ID)
}

const mediaFile = src => src?.split('?')[0].split('/').pop()

// getSizedImageUrl returns null for formats it does not parse, such as webp
const sizedImageUrl = (src, size) => getSizedImageUrl(src, size) ||
  (src && `${src}${src.includes('?') ? '&' : '?'}width=${parseInt(size, 10)}`)

const findTargetIndex = (media, { mediaId, file }) => {
  const idIndex = mediaId ? media.findIndex(item => item.id === mediaId) : -1
  if (idIndex !== -1 || !file) return idIndex
  return media.findIndex(item => mediaFile(item.preview_image?.src) === file)
}

const cansPositions = selection => [selection.flavourPosition, selection.packPosition].sort((a, b) => a - b)

const cansOptionsFor = (selection, variantId) => {
  const key = selection && Object.keys(selection.cells).find(cellKey => String(selection.cells[cellKey].variantId) === String(variantId))
  if (!key) return null
  const ids = key.split(',')
  return Object.fromEntries(cansPositions(selection).map((position, index) => [position, ids[index]]))
}

export const useProductStore = defineStore('ProductStore', {
  state: () => {
    return {
      product: {},
      combinedListingsProductVariants: [],
      combinedListingsProductVariantsMetafields: {},
      lastProduct: {},
      selectedVariant: {},
      selectedSellingPlan: null,
      backInStockPopupIsShowing: false,
      galleryOverrides: {},
      galleryOpensOnFlavour: false,
      cansSelection: null,
      selectedCansOptions: {},
    }
  },

  actions: {
    changeVariant (variant) {
      this.selectedVariant = variant
    },

    changeSellingPlan (sellingPlan) {
      this.selectedSellingPlan = sellingPlan
    },

    setInitialVariant () {
      let defaultVariant = this.product.variants[0]
      if (this.product.available) {
        defaultVariant = this.product.variants.find(variant => variant.available)
      }
      const serverVariant = this.product.variants.find(variant => variant.id === this.cansSelection?.selectedVariantId)
      this.selectedVariant = serverVariant || defaultVariant
      this.selectedCansOptions = cansOptionsFor(this.cansSelection, this.selectedVariant?.id) || {}
      
      // Update combined metafields after variant is set
      if (this.isCombinedListingsProduct) {
        // Wait for DOM to be ready, with retry logic
        this.updateCombinedMetafieldsWithRetry()
      }
    },

    setInitialProduct () {
      this.product = {
        ...window.theme.product,
        selling_plan_groups: filterSellingPlanGroups(window.theme.product.selling_plan_groups),
        metafields: { ...window.theme.productMetafields },
        inventoryQuantity: window.theme.productInventoryQuantity
      }
      this.galleryOverrides = window.theme.productGalleryOverrides || {}
      this.galleryOpensOnFlavour = window.theme.productGalleryOpensOnFlavour === true
      this.cansSelection = window.theme.cansSelectionState || null

      if (window.theme.combinedListingsProductVariants) {
        this.product.variants = window.theme.combinedListingsProductVariants.map(variantProduct => {
          const variantData = { ...variantProduct.variants[0] }
          variantData.featured_image = variantProduct.featured_image
          return variantData
        })

        this.combinedListingsProductVariants = window.theme.combinedListingsProductVariants.map(childProduct => ({
          ...childProduct,
          selling_plan_groups: filterSellingPlanGroups(childProduct.selling_plan_groups)
        }))
        this.combinedListingsProductVariantsMetafields = window.theme.combinedListingsProductVariantsMetafields
      }

      this.lastProduct = this.product

      this.updateJudgeMe()
      // Don't call updateCombinedMetafields here - it will be called in setInitialVariant()
    },

    async updateJudgeMe () {
      const productId = this.isCombinedListingsProduct ? this.combinedListingsProduct.id : this.product.id

      // Update all Judge.me widgets (badges and full review widget)
      const allWidgets = document.querySelectorAll('.jdgm-widget, .jdgm-preview-badge, #judgeme_product_reviews')
      allWidgets.forEach(widget => widget?.setAttribute('data-id', productId))

      setTimeout(() => {
        window.jdgm?.customizeBadges?.()
      }, 300)
    },

    async changeSelectedVariant (variantId) {
      const variant = this.product.variants.find(variant => variant.id === variantId)
      this.selectedVariant = variant
      const cansOptions = cansOptionsFor(this.cansSelection, variantId)
      if (cansOptions) this.selectedCansOptions = cansOptions
      // update URL
      // const newUrl = `/products/${this.product.handle}?variant=${variantId}`
      // history.replaceState({}, null, newUrl)    

      await yieldToMain()
      // update Judge.me
      await this.updateJudgeMe()
      // Update combined metafields with retry logic
      this.updateCombinedMetafieldsWithRetry()
    },

    selectCansOption (position, optionValueId) {
      const cell = this.cansCell(position, optionValueId)
      if (!cell || cell.hidden || !this.product.variants.some(variant => variant.id === cell.variantId)) return false

      this.changeSelectedVariant(cell.variantId).catch(error => console.error('[cans] deferred variant update failed:', error))
      return true
    },

    async changeProductByHandle ({ handle, metafields = null, inventoryQuantity = 99999 }) {
      document.body.classList.add('switching-product')
      
      const pageEndpoint = `${window.Shopify.routes.root}products/${handle}?view=product`
      await axios
        .get(pageEndpoint)
        .then(async response => {
          const pageHTML = response.data
          const temporaryWrapper = document.createElement('div')
          const liveProductSection = document.querySelector('.section-main-product')

          temporaryWrapper.innerHTML = pageHTML
          const productSection = temporaryWrapper.querySelector('.section-main-product')
          const dynamicBlocks = [...productSection.querySelectorAll('[data-block-index]')]

          dynamicBlocks.forEach(block => {
            let blockIndex = block.getAttribute('data-block-index')
            if (block.tagName === 'TEMPLATE') {
              blockIndex = parseInt(blockIndex) - 1
            }

            const liveBlock = liveProductSection.querySelector(`[data-block-index="${blockIndex}"]`)
            if (liveBlock) {
              liveBlock.innerHTML = block.innerHTML
            }
          })
          
        })
        .catch(error => {
          console.log(error)
        })

      const endpoint = `${window.Shopify.routes.root}products/${handle}.js`
      await axios
        .get(endpoint)
        .then(async response => {
          // update last product
          this.lastProduct = this.product

          // update product with filtered selling plan groups
          this.product = {
            ...response.data,
            selling_plan_groups: filterSellingPlanGroups(response.data.selling_plan_groups),
            metafields,
            inventoryQuantity
          }
          this.galleryOverrides = {}
          this.cansSelection = null

          // update selectedVariant
          this.setInitialVariant()

          // update URL
          const newUrl = `/products/${response.data.handle}`
          history.replaceState({}, null, newUrl)

          // update Judge.me
          await this.updateJudgeMe()
          // Update combined metafields with retry logic
          this.updateCombinedMetafieldsWithRetry()

          setTimeout(() => { document.body.classList.remove('switching-product') }, 300)
        })
        .catch(error => {
          console.log(error)

          setTimeout(() => { document.body.classList.remove('switching-product') }, 300)
        })
    },

    showBackInStockPopup () {
      this.backInStockPopupIsShowing = true
      document.body.classList.add('overflow-hidden')
    },

    hideBackInStockPopup (event = null) {
      if (!event || event.target.hasAttribute('data-close'))
        this.backInStockPopupIsShowing = false
        document.body.classList.remove('overflow-hidden')
    },

    updateCombinedMetafieldsWithRetry (retries = 10) {
      // Ensure variant is set
      if (!this.selectedVariant || !this.selectedVariant.id) {
        if (retries > 0) {
          setTimeout(() => {
            this.updateCombinedMetafieldsWithRetry(retries - 1)
          }, 100)
          return
        }
      }
      
      const elements = document.querySelectorAll('[data-combined-metafield]')
      
      if (elements.length === 0 && retries > 0) {
        // Elements not ready yet, retry
        setTimeout(() => {
          this.updateCombinedMetafieldsWithRetry(retries - 1)
        }, 100)
        return
      }
      
      // Elements found or max retries reached, proceed with update
      this.updateCombinedMetafields()
    },

    updateCombinedMetafields () {
      if (!this.isCombinedListingsProduct) {
        // Not a combined product, hide all combined metafield elements
        document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
          element.style.display = 'none'
        })
        return
      }

      // Ensure variant is set
      if (!this.selectedVariant || !this.selectedVariant.id) {
        // Variant not set yet, hide elements
        document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
          element.style.display = 'none'
        })
        return
      }

      const combined = this.combinedListingsProduct
      if (!combined || !combined.metafields) {
        // Hide all combined metafield elements if no combined product data
        document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
          element.style.display = 'none'
        })
        return
      }

      const combinedMetafields = {
        'text.short_description': combined.metafields.shortDescription,
        'descriptors.subtitle': combined.metafields.subtitle,
        'custom.offer_information': combined.metafields.offerInformation
      }

      document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
        const key = element.getAttribute('data-combined-metafield')
        const value = combinedMetafields[key]
        
        if (value && value.trim() !== '' && value !== 'null') {
          element.innerHTML = value
          element.style.display = ''
        } else {
          element.innerHTML = ''
          element.style.display = 'none'
        }
      })
    }
  },

  getters: {
    // productType (state) {
    //   let productType = ''
    //   state.product.tags.forEach(tag => {
    //     if (tag.includes('flavour:')) {
    //       productType = 'flavour'
    //     }
    //   })
    //   return productType
    // },

    isCombinedListingsProduct () {
      return this.product.tags?.includes('combined')
    },

    cansCell () {
      return (position, optionValueId) => {
        const selection = this.cansSelection
        if (!selection || !cansPositions(selection).includes(Number(position))) return null

        const options = { ...this.selectedCansOptions, [position]: String(optionValueId) }
        const ids = cansPositions(selection).map(optionPosition => options[optionPosition])
        return (ids.every(Boolean) && selection.cells[ids.join(',')]) || null
      }
    },

    isCansOptionSelected () {
      return (position, optionValueId) => this.selectedCansOptions[position] === String(optionValueId)
    },

    combinedListingsProduct () {
      if (!this.isCombinedListingsProduct) return null

      const product = this.combinedListingsProductVariants.find(product => product.variants.find(variant => variant.id === this.selectedVariant.id))
      const metafieldsProduct = this.combinedListingsProductVariantsMetafields.find(variant => variant.id === this.selectedVariant.id)
      
      return {
        ...product,
        metafields: {...metafieldsProduct?.productMetafields}
      }
    },

    selectedSellingPlanProduct () {
      if (!this.selectedVariant?.id) return null

      if (this.isCombinedListingsProduct) {
        return this.combinedListingsProductVariants.find(product =>
          product.variants?.some(variant => variant.id === this.selectedVariant.id)
        ) || null
      }

      return this.product
    },

    selectedChildHasSellingPlan () {
      const product = this.selectedSellingPlanProduct
      if (!product) return false

      const groups = filterSellingPlanGroups(product.selling_plan_groups)
      return groups.length > 0
    },

    subscriptionPerksVisible () {
      return this.selectedChildHasSellingPlan && this.selectedSellingPlan !== null
    },

    productHasSubscriptionOption () {
      return this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0
    },

    productHasPurchaseLimit () {
      return !!this.product.tags.find(tag => tag.includes('limit-'))
    },

    purchaseLimit () {
      return this.productHasPurchaseLimit ? parseInt(this.product.tags.find(tag => tag.includes('limit-')).replace('limit-', '')) : 999999
    },

    flavourCollectionHandle (state) {
      const servingTypes = window.theme.productTags.servingTypeTagKeys.split(',')
      let isValidServingType = false

      servingTypes.forEach(servingType => {
        const tag = window.theme.productTags[`${servingType}Tag`]
        if (state.product.tags.includes(tag)) {
          isValidServingType = true
        }
      })

      if (isValidServingType) {
        const flavourTag = state.product.tags.find(tag => tag.includes('flavour:'))
        if (flavourTag) {
          const flavourCollectionHandle = flavourTag.replace('flavour:', '')
          return flavourCollectionHandle
        }
      }

      return null
    },

    defaultVariant (state) {
      let defaultVariant = state.product.variants[0]
      if (state.product.available) {
        defaultVariant = state.product.variants.find(variant => variant.available)
      }
      return defaultVariant
    },

    shortDescription (state) {
      return state.product.metafields.shortDescription
    },

    priceFormatted (state) {
      if (state.selectedSellingPlan) {
        return state.selectedSellingPlan.price
      }
      return productPrice(state.product.price)
    },
    
    variantPriceFormatted (state) {
      if (state.selectedSellingPlan) {
        return state.selectedSellingPlan.price
      }

      return productPrice(state.selectedVariant.price)
    },

    saleBadge (state) {
      let saleBadge = null

      state.product.tags.forEach(tag => {
        if (tag.includes('promo-badge:')) {
          saleBadge = tag.split('promo-badge:')[1]
          return
        }
      })
      return saleBadge
    },

    badges (state) {
      let badges = []

      state.product.tags.forEach(tag => {
        if (tag.includes('badge-')) badges.push(tag.split('badge-')[1])
      })
      return badges.length > 0 ? badges : null
    },

    gradientBackgroundColours (state) {
      return state.product.metafields ? state.product.metafields.gradientBackgroundColours : null
    },

    hasColouredBgOption (state) {
      // let hasColouredBgOption = false
      // if (state.product.images[0].includes('.jpg')) {
      //   const colouredBgProductTagKeys = window.theme.productTags.colouredBgProductTagKeys.split(',')
      //   colouredBgProductTagKeys.forEach(tagKey => {
      //     const tag = window.theme.productTags[`${tagKey}Tag`]
      //     if (state.product.tags.includes(tag)) {
      //       hasColouredBgOption = true
      //       return
      //     }
      //   })
      // }
      //
      // return hasColouredBgOption

      return state.product.images[0].includes('.jpg') && state.product.images[1].includes('.png')
    },

    variantCollectionTriggerImage () {
      return sizedImageUrl(this.galleryMedia[0].preview_image.src, '48x')
    },

    variantTriggerImage () {
      return sizedImageUrl(this.selectedVariant.featured_image, '48x')
    },

    galleryTargets () {
      const targets = new Map()

      this.product.variants?.forEach(variant => {
        const override = this.galleryOverrides[String(variant.id)]
        if (override?.mediaId || override?.src) {
          targets.set(variant.id, { mediaId: override.mediaId || null, file: mediaFile(override.src) })
          return
        }

        if (this.isCombinedListingsProduct) {
          const child = this.combinedListingsProductVariants.find(product => product.variants?.some(childVariant => childVariant.id === variant.id))
          const childMedia = child?.media?.find(media => media.media_type === 'image')
          if (childMedia) targets.set(variant.id, { mediaId: childMedia.id, file: mediaFile(childMedia.preview_image?.src) })
          return
        }

        if (variant.featured_media) {
          targets.set(variant.id, { mediaId: variant.featured_media.id, file: mediaFile(variant.featured_media.preview_image?.src) })
        } else if (variant.featured_image) {
          targets.set(variant.id, { mediaId: null, file: mediaFile(variant.featured_image.src || variant.featured_image) })
        }
      })

      return targets
    },

    selectedVariantGalleryIndex () {
      const target = this.galleryTargets.get(this.selectedVariant?.id)
      if (!target) return null

      // largeMedia maps galleryMedia 1:1, so the index is shared
      const index = findTargetIndex(this.galleryMedia, target)
      return index === -1 ? null : index
    },

    galleryMedia (state) {
      const targetIds = new Set()
      const targetFiles = new Set()
      this.galleryTargets.forEach(({ mediaId, file }) => {
        if (mediaId) targetIds.add(mediaId)
        if (file) targetFiles.add(file)
      })
      const restored = new Set()

      const media = state.product.media.filter((image, index) => {
        // First filter out the coloured background option if needed
        if (this.hasColouredBgOption && index === 0) {
          return false
        }

        // Then handle combined listings product filtering
        if (this.isCombinedListingsProduct) {
          const formattedImageSrc = image.preview_image.src.split('?')[0].split('files/')[1]
          const isVariantImage = this.combinedListingsProductVariants.some(variant => {
            const variantMedia = variant.media.find(media => media.media_type === 'image')
            if (!variantMedia) return false
            const formattedVariantImageSrc = variantMedia.preview_image.src.split('?')[0].split('files/')[1]
            return formattedImageSrc === formattedVariantImageSrc
          })
          if (!isVariantImage) return true

          if (targetIds.has(image.id) || targetFiles.has(mediaFile(image.preview_image?.src))) {
            restored.add(image)
            return true
          }
          return false
        }

        return true
      })

      // Restored flavour images never take the lead slide from the gallery's own first image
      const leadIndex = media.findIndex(image => !restored.has(image))
      if (leadIndex > 0) media.unshift(...media.splice(leadIndex, 1))

      return media
    },

    thumbnailImages() {
      return this.galleryMedia.map((m) =>
        sizedImageUrl(m.preview_image.src, '246x')
      )
    },

    largeMedia () {
      return this.galleryMedia.map((image) => ({
        ...image,
        preview_image: {
          ...image.preview_image,
          src: sizedImageUrl(image.preview_image.src, '1400x'),
          mobileSrc: sizedImageUrl(image.preview_image.src, '600x'),
        },
      }))
    },
  },
})
